/**
 * Report Amend Page
 *
 * Amend a finalized radiology report
 */

import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  ArrowLeft,
  FilePlus,
  Loader2,
  AlertCircle,
  AlertTriangle,
} from 'lucide-react'

import {
  useReport,
  useAmendReport,
  canAmendReport,
  formatReportDateTime,
} from '@/application/reports'
import { useStudy } from '@/application/studies'
import { usePatient } from '@/application/patients'
import { Button } from '@/presentation/components/ui/Button'
import { Card, CardContent, CardHeader, CardTitle } from '@/presentation/components/ui/Card'
import { VoiceDictationButton } from '@/presentation/components/VoiceDictation'
import { toast } from '@/lib/toast'

// =============================================================================
// Component
// =============================================================================

export function ReportAmendPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()

  // Form state
  const [formData, setFormData] = useState({
    findings: '',
    impression: '',
    recommendations: '',
    amendmentReason: '',
  })
  const [errors, setErrors] = useState<Record<string, string>>({})

  // Queries
  const { data: originalReport, isLoading: reportLoading } = useReport(id)
  const { data: study } = useStudy(originalReport?.studyId)
  const { data: patient } = usePatient(study?.patientId)

  // Mutation
  const amendMutation = useAmendReport()

  // Populate form with original report data
  useEffect(() => {
    if (originalReport) {
      setFormData({
        findings: originalReport.findings,
        impression: originalReport.impression,
        recommendations: originalReport.recommendations || '',
        amendmentReason: '',
      })
    }
  }, [originalReport])

  // Check if amending is allowed
  useEffect(() => {
    if (originalReport && !canAmendReport(originalReport.status)) {
      navigate(`/reports/${id}`, { replace: true })
    }
  }, [originalReport, id, navigate])

  const handleChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }))
    }
  }

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {}

    if (!formData.findings.trim()) {
      newErrors.findings = 'Los hallazgos son requeridos'
    }
    if (!formData.impression.trim()) {
      newErrors.impression = 'La impresión diagnóstica es requerida'
    }
    if (!formData.amendmentReason.trim()) {
      newErrors.amendmentReason = 'El motivo de la enmienda es requerido'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validate() || !id) return

    try {
      const amendedReport = await amendMutation.mutateAsync({
        id,
        data: {
          findings: formData.findings,
          impression: formData.impression,
          recommendations: formData.recommendations || undefined,
          amendmentReason: formData.amendmentReason,
        },
      })
      toast.success('Informe enmendado correctamente')
      navigate(`/reports/${amendedReport.id}`)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Error al enmendar el informe')
    }
  }

  if (reportLoading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-[var(--primary)] mx-auto mb-4" />
          <p className="text-[var(--foreground-muted)]">Cargando reporte...</p>
        </div>
      </div>
    )
  }

  if (!originalReport) {
    return (
      <div className="flex flex-col items-center justify-center h-[60vh] text-center">
        <AlertCircle className="h-12 w-12 text-red-500 mb-4" />
        <h2 className="text-xl font-semibold text-[var(--foreground)] mb-2">
          Reporte no encontrado
        </h2>
        <Button variant="outline" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Volver
        </Button>
      </div>
    )
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-[var(--foreground)]">
            Enmendar Reporte
          </h1>
          <p className="text-[var(--foreground-muted)]">
            Crear una enmienda del reporte firmado
          </p>
        </div>
      </div>

      {/* Warning */}
      <Card className="alert-warning border-0">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 mt-0.5" />
            <div>
              <p className="font-medium">Importante</p>
              <p className="text-sm mt-1">
                Al enmendar este reporte, se creará una nueva versión. El reporte original
                quedará marcado como enmendado y seguirá siendo accesible en el historial.
                Debe proporcionar un motivo válido para la enmienda.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Original Report Info */}
      <Card className="bg-[var(--background-subtle)]">
        <CardHeader>
          <CardTitle className="text-base">Reporte Original</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm">
          <p>
            <span className="text-[var(--foreground-muted)]">Título:</span>{' '}
            {originalReport.title}
          </p>
          <p>
            <span className="text-[var(--foreground-muted)]">Versión:</span>{' '}
            {originalReport.version}
          </p>
          <p>
            <span className="text-[var(--foreground-muted)]">Firmado:</span>{' '}
            {formatReportDateTime(originalReport.signedAt)}
          </p>
          {patient && (
            <p>
              <span className="text-[var(--foreground-muted)]">Paciente:</span>{' '}
              {patient.firstName} {patient.lastName}
            </p>
          )}
        </CardContent>
      </Card>

      {/* Error message */}
      {amendMutation.isError && (
        <Card className="border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950/40">
          <CardContent className="p-4">
            <div className="flex items-center gap-3 text-red-700 dark:text-red-300">
              <AlertCircle className="h-5 w-5" />
              <span>
                {amendMutation.error?.message || 'Error al enmendar el reporte'}
              </span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit}>
        <Card>
          <CardHeader>
            <CardTitle>Contenido de la Enmienda</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Amendment Reason */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium text-[var(--foreground)]">
                  Motivo de la Enmienda *
                </label>
                <VoiceDictationButton
                  onTranscript={(text) => handleChange('amendmentReason', formData.amendmentReason + ' ' + text)}
                  disabled={amendMutation.isPending}
                />
              </div>
              <textarea
                value={formData.amendmentReason}
                onChange={(e) => handleChange('amendmentReason', e.target.value)}
                placeholder="Explique el motivo de la enmienda..."
                rows={3}
                className={`w-full rounded-md border ${
                  errors.amendmentReason ? 'border-red-500' : 'border-[var(--border)]'
                } bg-[var(--background)] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--ring)]`}
              />
              {errors.amendmentReason && (
                <p className="text-sm text-red-500 mt-1">{errors.amendmentReason}</p>
              )}
            </div>

            <hr className="border-[var(--border)]" />

            {/* Findings */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium text-[var(--foreground)]">
                  Hallazgos Corregidos *
                </label>
                <VoiceDictationButton
                  onTranscript={(text) => handleChange('findings', formData.findings + ' ' + text)}
                  disabled={amendMutation.isPending}
                />
              </div>
              <textarea
                value={formData.findings}
                onChange={(e) => handleChange('findings', e.target.value)}
                placeholder="Describa los hallazgos corregidos..."
                rows={8}
                className={`w-full rounded-md border ${
                  errors.findings ? 'border-red-500' : 'border-[var(--border)]'
                } bg-[var(--background)] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--ring)]`}
              />
              {errors.findings && (
                <p className="text-sm text-red-500 mt-1">{errors.findings}</p>
              )}
            </div>

            {/* Impression */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium text-[var(--foreground)]">
                  Impresión Diagnóstica Corregida *
                </label>
                <VoiceDictationButton
                  onTranscript={(text) => handleChange('impression', formData.impression + ' ' + text)}
                  disabled={amendMutation.isPending}
                />
              </div>
              <textarea
                value={formData.impression}
                onChange={(e) => handleChange('impression', e.target.value)}
                placeholder="Conclusión diagnóstica corregida..."
                rows={4}
                className={`w-full rounded-md border ${
                  errors.impression ? 'border-red-500' : 'border-[var(--border)]'
                } bg-[var(--background)] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--ring)]`}
              />
              {errors.impression && (
                <p className="text-sm text-red-500 mt-1">{errors.impression}</p>
              )}
            </div>

            {/* Recommendations */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium text-[var(--foreground)]">
                  Recomendaciones
                </label>
                <VoiceDictationButton
                  onTranscript={(text) => handleChange('recommendations', formData.recommendations + ' ' + text)}
                  disabled={amendMutation.isPending}
                />
              </div>
              <textarea
                value={formData.recommendations}
                onChange={(e) => handleChange('recommendations', e.target.value)}
                placeholder="Recomendaciones adicionales (opcional)..."
                rows={3}
                className="w-full rounded-md border border-[var(--border)] bg-[var(--background)] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--ring)]"
              />
            </div>

            {/* Actions */}
            <div className="flex items-center justify-end gap-4 pt-4 border-t border-[var(--border)]">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate(-1)}
                disabled={amendMutation.isPending}
              >
                Cancelar
              </Button>
              <Button type="submit" disabled={amendMutation.isPending}>
                {amendMutation.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Procesando...
                  </>
                ) : (
                  <>
                    <FilePlus className="h-4 w-4 mr-2" />
                    Crear Enmienda
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      </form>
    </div>
  )
}
